import React, { useEffect, useState } from 'react';
import { FaQrcode, FaFilePdf, FaTimes, FaUser, FaIdCard, FaCalendarCheck, FaExclamationTriangle } from 'react-icons/fa';
import { EntradaAfiliadoDto } from '../types';
import congresoService from '../services/congresoService';

interface EntradaDigitalModalProps {
  isOpen: boolean;
  onClose: () => void;
  entrada: EntradaAfiliadoDto | null;
  idAfiliado?: number;
  onDescargarPdf: (idEntradaDetalle: number) => Promise<void>;
  descargandoPdf?: boolean;
}

export const EntradaDigitalModal: React.FC<EntradaDigitalModalProps> = ({
  isOpen,
  onClose,
  entrada,
  idAfiliado = 12345,
  onDescargarPdf,
  descargandoPdf = false
}) => {
  const [qrBlobUrl, setQrBlobUrl] = useState<string | null>(null);
  const [cargandoQr, setCargandoQr] = useState<boolean>(false);
  const [errorQr, setErrorQr] = useState<string | null>(null);

  useEffect(() => {
    let urlActiva: string | null = null;

    if (isOpen && entrada) {
      setCargandoQr(true);
      setErrorQr(null);
      setQrBlobUrl(null);

      congresoService.obtenerQrCodeBlob(entrada.idEntradaDetalle, idAfiliado)
        .then((blob) => {
          urlActiva = URL.createObjectURL(blob);
          setQrBlobUrl(urlActiva);
        })
        .catch((err) => {
          console.error('Error al recuperar QR autenticado:', err);
          setErrorQr('No se pudo cargar el código QR de acceso.');
        })
        .finally(() => {
          setCargandoQr(false);
        });
    }

    return () => {
      if (urlActiva) {
        URL.revokeObjectURL(urlActiva);
      }
    };
  }, [isOpen, entrada, idAfiliado]);

  if (!isOpen || !entrada) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]"
      onClick={onClose}
      data-testid="entrada-digital-modal-backdrop"
    >
      <div 
        className="bg-white dark:bg-boxdark rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 dark:border-strokedark overflow-hidden flex flex-col transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado con insignia corporativa de alto contraste */}
        <div className="bg-primario-azul-900 px-6 py-4 flex items-center justify-between text-white border-b border-primario-azul-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white shadow-sm flex-shrink-0">
              <FaQrcode className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-base text-white">Pase Digital de Acceso</h3>
              <p className="text-xs text-blue-200">{entrada.nombreCongreso}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar modal"
            className="p-1.5 rounded-full hover:bg-white/20 text-white transition duration-150"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Cuerpo del Pase Digital */}
        <div className="p-6 flex flex-col items-center space-y-5">
          {/* Badge del Ticket */}
          <div className="text-center space-y-1">
            <span className="inline-block px-3.5 py-1 text-xs font-mono font-bold text-primario-celeste-800 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-700/50 rounded-full">
              Ticket #{entrada.numeroEntrada}
            </span>
          </div>

          {/* Tarjeta de Código QR Oficial Autenticado */}
          <div className="bg-white p-4 rounded-2xl shadow-inner border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center min-h-[220px] w-full max-w-[240px]">
            {cargandoQr && (
              <div className="flex flex-col items-center space-y-2 py-8">
                <div className="w-8 h-8 border-4 border-primario-celeste-800 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-slate-500 font-semibold">Generando QR oficial...</span>
              </div>
            )}

            {errorQr && !cargandoQr && (
              <div className="flex flex-col items-center space-y-2 text-rose-600 text-xs text-center p-3">
                <FaExclamationTriangle className="w-6 h-6" />
                <span>{errorQr}</span>
              </div>
            )}

            {qrBlobUrl && !cargandoQr && (
              <>
                <img 
                  src={qrBlobUrl} 
                  alt={`Código QR para ticket ${entrada.numeroEntrada}`} 
                  className="w-48 h-48 object-contain rounded-lg"
                  data-testid="qr-code-image"
                />
                <p className="text-[11px] font-semibold text-slate-500 mt-2 text-center">
                  Presentá este código en el mostrador de acreditación
                </p>
              </>
            )}
          </div>

          {/* Datos del Portador / Asistente */}
          <div className="w-full bg-slate-50 dark:bg-meta-4 p-4 rounded-2xl border border-slate-100 dark:border-strokedark space-y-2 text-xs">
            <div className="flex items-center space-x-2 text-slate-700 dark:text-slate-200">
              <FaUser className="text-primario-celeste-800 flex-shrink-0" />
              <span>
                <strong className="text-slate-900 dark:text-white">Portador: </strong>
                {entrada.portadorNombre || <span className="text-amber-600 dark:text-amber-400 font-semibold">⚠️ Pendiente de nominación</span>}
              </span>
            </div>
            {entrada.portadorDni && (
              <div className="flex items-center space-x-2 text-slate-700 dark:text-slate-200">
                <FaIdCard className="text-primario-celeste-800 flex-shrink-0" />
                <span>
                  <strong className="text-slate-900 dark:text-white">DNI: </strong>
                  {entrada.portadorDni}
                </span>
              </div>
            )}
            {entrada.acreditada && (
              <div className="flex items-center space-x-2 text-emerald-700 dark:text-emerald-400 font-bold">
                <FaCalendarCheck className="flex-shrink-0" />
                <span>✓ Acreditado en el evento</span>
              </div>
            )}
          </div>

          {/* Acciones del Pase */}
          <div className="w-full space-y-2">
            <button
              onClick={() => onDescargarPdf(entrada.idEntradaDetalle)}
              disabled={descargandoPdf}
              className="w-full py-3 px-4 text-xs font-bold text-white bg-primario-celeste-800 hover:bg-primario-azul-900 rounded-xl transition duration-150 flex items-center justify-center space-x-2 shadow-sm"
            >
              {descargandoPdf ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generando PDF oficial...</span>
                </>
              ) : (
                <>
                  <FaFilePdf className="w-4 h-4 text-rose-300" />
                  <span>Descargar Ticket en PDF</span>
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition duration-150"
            >
              Cerrar Pase
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
